import React from "react";
import {
  AbsoluteFill,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { loadFont } from "@remotion/google-fonts/JetBrainsMono";
import { COLOR, LOGO } from "../../../brand/theme";

// Brand display font — JetBrains Mono (official accent font, brandbook 2026).
const { fontFamily } = loadFont("normal", { weights: ["400", "700", "800"] });

export type SermonQuoteProps = {
  quote: string;
  speaker: string;
  sermonTitle: string;
  reference?: string;
};

export const SermonQuote: React.FC<SermonQuoteProps> = ({
  quote,
  speaker,
  sermonTitle,
  reference,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const enter = spring({ frame, fps, config: { damping: 200 } });
  const quoteOpacity = interpolate(frame, [0, 22], [0, 1], { extrapolateRight: "clamp" });
  const quoteY = interpolate(enter, [0, 1], [34, 0]);
  const metaOpacity = interpolate(frame, [18, 40], [0, 1], { extrapolateRight: "clamp" });
  const logoOpacity = interpolate(frame, [12, 38], [0, LOGO.watermark.opacity], {
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill style={{ backgroundColor: COLOR.black, fontFamily }}>
      {/* Soft brand glow — depth without clutter */}
      <AbsoluteFill
        style={{
          background: `radial-gradient(circle at 18% 12%, ${COLOR.blue}22 0%, transparent 42%)`,
        }}
      />

      <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", padding: 160 }}>
        <div
          style={{
            opacity: quoteOpacity,
            transform: `translateY(${quoteY}px)`,
            maxWidth: 1460,
            display: "flex",
            gap: 40,
            alignItems: "flex-start",
          }}
        >
          {/* Brand accent bar */}
          <div
            style={{ width: 8, alignSelf: "stretch", backgroundColor: COLOR.blue, borderRadius: 4 }}
          />
          <div>
            <div
              style={{
                color: COLOR.white,
                fontSize: 58,
                fontWeight: 800,
                lineHeight: 1.28,
                letterSpacing: -1,
              }}
            >
              {quote}
            </div>

            <div style={{ opacity: metaOpacity, marginTop: 52 }}>
              <div
                style={{ width: 64, height: 4, backgroundColor: COLOR.blue, borderRadius: 2, marginBottom: 22 }}
              />
              <div style={{ color: COLOR.white, fontSize: 34, fontWeight: 700 }}>{speaker}</div>
              <div style={{ color: COLOR.blue300, fontSize: 26, fontWeight: 400, marginTop: 8 }}>
                {sermonTitle}
                {reference ? `  ·  ${reference}` : ""}
              </div>
            </div>
          </div>
        </div>
      </AbsoluteFill>

      {/* Logo watermark — white on dark, per brandbook */}
      <Img
        src={staticFile(LOGO.markAsset)}
        style={{
          position: "absolute",
          bottom: 72,
          right: 96,
          width: 104,
          height: 104,
          opacity: logoOpacity,
          filter: LOGO.whiteOnDarkFilter,
        }}
      />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
